import React, { useState, useMemo, useEffect } from 'react';
import { useTimeClock } from '../hooks/useTimeClock';
import { Employee, Shift, EmployeeSchedule, EmployeeRole, PayrollFrequency, PayrollSettings, DisplayGroup } from '../../shared/types.js';
import Button from '../components/Button';
import { cloneDeep, correctDateForTimezone } from '../utils.js';

interface ScheduleDayInfo {
    label: string;
    dayIndex: number;
}

const getScheduleCycleInfo = (settings: PayrollSettings): ScheduleDayInfo[] => {
    const info: ScheduleDayInfo[] = [];
    if (!settings.startDate) return [];
    const start = correctDateForTimezone(settings.startDate);
    const startDayOfWeek = start.getDay();
    const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    let numberOfDays = 0;
    switch (settings.frequency) {
        case PayrollFrequency.WEEKLY:
            numberOfDays = 7;
            break;
        case PayrollFrequency.BIWEEKLY:
            numberOfDays = 14;
            break;
        default:
            return [];
    }
    for (let i = 0; i < numberOfDays; i++) {
        const dayOfWeek = (startDayOfWeek + i) % 7;
        info.push({
            label: `${dayNames[dayOfWeek]}`,
            dayIndex: i,
        });
    }
    return info;
};

const SchedulesSettingsTab: React.FC = () => {
    const { employees, shifts, schedules, updateEmployeeSchedule, payrollSettings, displayGroups } = useTimeClock();
    const [selectedEmployeeId, setSelectedEmployeeId] = useState<string | null>(null);
    const [editingSchedule, setEditingSchedule] = useState<EmployeeSchedule>([]);
    const scheduleCycleDays = useMemo(() => getScheduleCycleInfo(payrollSettings), [payrollSettings]);
    const isScheduleFeatureEnabled = [PayrollFrequency.WEEKLY, PayrollFrequency.BIWEEKLY].includes(payrollSettings.frequency);
    const employeesInAnyGroup = useMemo(() => {
        const groupEmployeeIds = new Set(displayGroups.flatMap(g => g.employeeIds));
        return employees.filter(e => groupEmployeeIds.has(e.id));
    }, [employees, displayGroups]);
    const sortedEmployees = useMemo(() => {
        return [...employeesInAnyGroup]
            .filter(e => e.role !== EmployeeRole.INACTIVE)
            .sort((a,b) => (a.lastName || '').localeCompare(b.lastName || '') || (a.firstName || '').localeCompare(b.firstName || ''));
    }, [employeesInAnyGroup]);
    useEffect(() => {
        if (!selectedEmployeeId && sortedEmployees.length > 0) {
            setSelectedEmployeeId(sortedEmployees[0].id);
        } else if (selectedEmployeeId && !sortedEmployees.some(u => u.id === selectedEmployeeId)) {
            setSelectedEmployeeId(sortedEmployees[0]?.id || null);
        }
    }, [sortedEmployees, selectedEmployeeId]);
    const selectedEmployee = useMemo(() => {
        return employees.find(e => e.id === selectedEmployeeId);
    }, [selectedEmployeeId, employees]);
    const originalSchedule = useMemo(() => {
        if (!selectedEmployeeId) return [];
        const savedSchedule = schedules[selectedEmployeeId] || [];
        const cycleLength = scheduleCycleDays.length;
        if (savedSchedule.length !== cycleLength) {
            const resizedSchedule = Array(cycleLength).fill(null);
            for(let i=0; i < Math.min(savedSchedule.length, cycleLength); i++) {
                resizedSchedule[i] = savedSchedule[i];
            }
            return resizedSchedule;
        }
        return savedSchedule;
    }, [selectedEmployeeId, scheduleCycleDays.length, JSON.stringify(schedules[selectedEmployeeId])]);
    useEffect(() => {
        setEditingSchedule(cloneDeep(originalSchedule));
    }, [originalSchedule]);
    const isDirty = useMemo(() => {
        return JSON.stringify(originalSchedule) !== JSON.stringify(editingSchedule);
    }, [originalSchedule, editingSchedule]);
    const handleScheduleChange = (dayIndex: number, shiftId: string | null) => {
        setEditingSchedule(prev => {
            const newSchedule = [...(prev || [])];
            newSchedule[dayIndex] = shiftId;
            return newSchedule;
        });
    };
    const handleClearSchedule = () => {
        setEditingSchedule(Array(scheduleCycleDays.length).fill(null));
    };
	const DEFAULT_SHIFT = "default-shift"
	const handleFillSchedule = () => {
        setEditingSchedule(Array(scheduleCycleDays.length).fill(DEFAULT_SHIFT));
    };
    const handleRevert = () => {
        setEditingSchedule(cloneDeep(originalSchedule));
    };
    const handleApply = () => {
        if (selectedEmployeeId) {
            updateEmployeeSchedule(selectedEmployeeId, editingSchedule);
        }
    };
    const renderScheduleEditor = () => {
        if (!isScheduleFeatureEnabled) {
            return (
                <div className="flex flex-col items-center justify-center h-full p-6 text-center text-gray-500 dark:text-gray-400">
                    <p className="text-lg">Repeating schedules can only be set for Weekly or Bi-Weekly pay periods.</p>
                    <p>Your current setting is: <span className="font-semibold text-gray-900 dark:text-white">{payrollSettings.frequency}</span>.</p>
                    <p className="mt-4">Please change your payroll settings to use this feature.</p>
                </div>
            )
        }
        if (!selectedEmployee) {
             return (
                 <div className="flex items-center justify-center h-full text-gray-500">
                     {employees.length > 0 ? "Select an employee to view their schedule" : "You need at least one employee before managing schedules."}
                 </div>
             )
        }
        const isBiWeekly = payrollSettings.frequency === PayrollFrequency.BIWEEKLY;
        const week1Days = isBiWeekly ? scheduleCycleDays.slice(0, 7) : scheduleCycleDays;
        const week2Days = isBiWeekly ? scheduleCycleDays.slice(7) : [];
        const DayScheduleInputs = ({ days }: { days: ScheduleDayInfo[] }) => (
            <div className="space-y-3">
                {days.map(({ label, dayIndex }) => (
                    <div key={dayIndex} className="grid grid-cols-2 items-center gap-4">
                        <label htmlFor={`shift-${dayIndex}`} className="text-right font-medium text-gray-600 dark:text-gray-300">
                            {label}
                        </label>
                        <select
                            id={`shift-${dayIndex}`}
                            value={editingSchedule[dayIndex] || ''}
                            onChange={(e) => handleScheduleChange(dayIndex, e.target.value || null)}
                            className="block w-full bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-brand-secondary focus:border-brand-secondary"
                        >
                            <option value="">-- No Shift --</option>
                            {shifts.map(shift => (
                                <option key={shift.id} value={shift.id}>{shift.name}</option>
                            ))}
                        </select>
                    </div>
                ))}
            </div>
        );
        return (
            <>
                <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                    <h2 className="text-2xl font-bold">Schedule for {selectedEmployee.firstName} {selectedEmployee.lastName}</h2>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                        Pay Period Cycle: {payrollSettings.frequency} ({scheduleCycleDays.length} days)
                    </p>
                </div>
                <div className="flex-grow p-6 space-y-4 overflow-y-auto">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12">
                        <div className="max-w-sm">
                            <h3 className="text-lg font-semibold mb-2 text-gray-500 dark:text-gray-400">{isBiWeekly ? 'Week 1' : 'Assigned Shifts'}</h3>
                            <DayScheduleInputs days={week1Days} />
                        </div>
                        {isBiWeekly && (
                             <div className="max-w-sm">
                                <h3 className="text-lg font-semibold mb-2 text-gray-500 dark:text-gray-400">Week 2</h3>
                                <DayScheduleInputs days={week2Days} />
                            </div>
                        )}
                    </div>
                     <div className="flex justify-start mt-8 gap-4">
                        <Button variant="danger" onClick={handleClearSchedule}>Clear Schedule</Button>
						<Button variant="secondary" onClick={handleFillSchedule}>Fill Schedule</Button>
                    </div>
                </div>
                <div className="p-4 border-t border-gray-200 dark:border-gray-700 bg-gray-100 dark:bg-gray-900/40 flex justify-end gap-4">
                    <Button variant="secondary" onClick={handleRevert} disabled={!isDirty}>Revert</Button>
                    <Button onClick={handleApply} disabled={!isDirty}>Apply</Button>
                </div>
            </>
        );
    };
    return (
        <div className="flex bg-white dark:bg-gray-800 text-gray-900 dark:text-white rounded-lg shadow-lg h-[calc(100vh-22rem)]">
            <div className="w-1/3 md:w-1/4 flex flex-col border-r border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
                <div className="p-4 border-b border-gray-200 dark:border-gray-700 text-lg font-semibold">
                    Employees
                </div>
                <div className="flex-grow p-2 space-y-1 overflow-y-auto">
                    {sortedEmployees.map(employee => (
                        <button
                            key={employee.id}
                            onClick={() => setSelectedEmployeeId(employee.id)}
                            className={`w-full text-left p-3 rounded-lg transition-colors ${selectedEmployeeId === employee.id ? 'bg-brand-secondary text-white font-bold' : 'hover:bg-gray-200 dark:hover:bg-gray-700'}`}
                        >
                            {`${employee.lastName}, ${employee.firstName}`}
                        </button>
                    ))}
                </div>
            </div>
            <div className="w-2/3 md:w-3/4 flex flex-col">
                {renderScheduleEditor()}
            </div>
        </div>
    );
};

export default SchedulesSettingsTab;